<template>
  <v-parallax class="hero" src="/img/hero-room.webp" alt="Jasmine Therapies treatment room">
    <div class="hero-banner fill-height d-flex flex-column justify-center">
      <div class="intro-text d-flex flex-column justify-center pt-0 pa-8">
        <div class="text-center text-subtitle-1 pa-2">You are one massage away from</div>
        <div class="text-center text-h2 pa-2">deep relaxation</div>
      </div>
      <div class="text-center">
        <v-btn class="book-now-button" color="primary" size="large" :href="$bookingUrls.RelaxingMassage.Default">Book now!</v-btn>
      </div>
    </div>
  </v-parallax>

  <section id="why-choose">
    <div class="full-width-header">Why choose our relaxing massage?</div>
    <div class="content pb-16">
      <div class="feature-grid">
        <div class="feature-item" v-for="item in whyChoose" :key="item.title">
          <div class="feature-title">{{ item.title }}</div>
          <div class="feature-text">{{ item.description }}</div>
        </div>
      </div>

      <div class="text-center">
        <v-btn class="book-now-button" color="primary" size="large" :href="$bookingUrls.RelaxingMassage.Default">Book now!</v-btn>
      </div>
    </div>
  </section>

  <section id="trusted-by">
    <div class="full-width-header">Trusted by our clients</div>
    <div class="content pb-16">
      <div class="quote-block" v-for="review in featureReviews" :key="review.name">
        <img class="quote-stars" :src="starsImage" alt="Five stars" />
        <p class="quote-text">"{{ review.quote }}"</p>
        <div class="quote-author">{{ review.name }}</div>
      </div>

      <div class="text-center">
        <v-btn class="book-now-button" color="primary" size="large" :href="$bookingUrls.RelaxingMassage.Default">Book now!</v-btn>
      </div>
    </div>
  </section>

  <BenefitsSectionComponent
    header="What are the benefits of Relaxing Massage?"
    :benefits="benefits"
    :booking-url="$bookingUrls.RelaxingMassage.Default" />

  <section id="pricing">
    <div class="full-width-header">Individual Sessions & Packages</div>
    <div class="content pb-16">
      <p class="content-intro">
        Choose between our convenient package options or flexible individual sessions. Book now to experience a calming,
        restorative massage tailored to your needs.
      </p>

      <div class="service-list d-flex flex-wrap justify-center ga-12 px-8">
        <ServiceItemComponent
          title="5-Session Packages"
          :booking-options="packageOptions"
          :link-target="'_self'"
          class="service-item-card" />

        <ServiceItemComponent
          title="Individual Sessions"
          :booking-options="sessionOptions"
          :link-target="'_self'"
          class="service-item-card" />
      </div>
    </div>
  </section>

  <section id="cta">
    <div class="full-width-header">Ready to feel completely relaxed?</div>
    <div class="content pb-16">
      <p class="content-intro">
        Take a moment for yourself and let go of tension. Book your relaxing massage session today.
      </p>
      <div class="text-center">
        <v-btn class="book-now-button" color="primary" size="large" :href="$bookingUrls.RelaxingMassage.Default">Book now!</v-btn>
      </div>
    </div>
  </section>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import { BookingUrls } from '@/constants/booking';
import BookingOption from '@/models/BookingOption';
import BenefitsSectionComponent from '@/components/BenefitsSectionComponent.vue';
import ServiceItemComponent from '@/components/ServiceItemComponent.vue';

export default defineComponent({
  name: 'RelaxingMassageView',

  components: {
    BenefitsSectionComponent,
    ServiceItemComponent
  },

  data() {
    return {
      whyChoose: [
        {
          title: 'Gentle, calming techniques',
          description: 'Slow, flowing strokes help quiet the mind and ease everyday stress.'
        },
        {
          title: 'Comfort-first approach',
          description: 'We tailor pressure and positioning to keep you fully relaxed throughout your session.'
        },
        {
          title: 'A serene environment',
          description: 'A peaceful space designed to support deep rest and renewal.'
        }
      ],
      starsImage: '/img/five-stars.png',
      featureReviews: [
        {
          name: 'Dee Sweeney',
          quote:
            'Jasmine is fantastic...so knowledgable and also such a lovely person. I always enjoy my experienc here. Thank You Jasmine'
        }
      ],
      benefits: [
        {
          title: 'Deep Stress Relief',
          description: 'Slow, flowing techniques calm the nervous system and dissolve the tension of everyday life.'
        },
        {
          title: 'Eases Muscle Tightness',
          description: 'Gentle pressure releases built-up tightness without the intensity of deep tissue work.'
        },
        {
          title: 'Improves Sleep Quality',
          description: 'Promotes a state of deep rest that helps you sleep better and wake feeling refreshed.'
        }
      ],
      packageOptions: [
        new BookingOption('5 x 50 min', BookingUrls.RelaxingMassage.Package50),
        new BookingOption('5 x 1h 15 min', BookingUrls.RelaxingMassage.Package75)
      ],
      sessionOptions: [
        new BookingOption('25 min', BookingUrls.RelaxingMassage.Min25),
        new BookingOption('50 min', BookingUrls.RelaxingMassage.Min50),
        new BookingOption('1h 15 min', BookingUrls.RelaxingMassage.Min75)
      ]
    };
  }
});
</script>
